import {
  compareAsc,
  eachDayOfInterval,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isValid,
  isWeekend,
  isWithinInterval,
  parse,
  set,
  startOfMonth,
} from 'date-fns'

export type DateInterval = {
  start: Date
  end: Date
}

export const businessDaysBetween = (interval: DateInterval): number =>
  eachDayOfInterval(interval).filter((day) => !isWeekend(day)).length - 1

export const isWithinTimeInterval = (date: Date, interval: DateInterval): boolean => {
  // isWithinInterval checks date & time,
  // so we have to be sure that de date is the same for all
  const commonDate = new Date()

  return isWithinInterval(
    set(commonDate, { hours: date.getHours(), minutes: date.getMinutes(), seconds: 0 }),
    {
      start: set(commonDate, { hours: interval.start.getHours(), minutes: 0, seconds: 0 }),
      end: set(commonDate, { hours: interval.end.getHours(), minutes: 0, seconds: 0 }),
    },
  )
}

export const daysBetween = (interval: DateInterval): number =>
  eachDayOfInterval(interval).length - 1

export const dateIsGreater = (dateLeft: Date | number, dateRight: Date | number): boolean =>
  compareAsc(dateLeft, dateRight) === 1

export const formatToShortDate = (date: string) => {
  const shortDate = date.split('T')[0]
  return shortDate
}

export const dateTimeZoneFormat = (date: Date | null, order: 'start' | 'end'): Date | null => {
  if (!date) {
    return null
  }

  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()

  const dateString = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`

  if (order === 'start') {
    return new Date(`${dateString}T00:00:00Z`)
  } else {
    return new Date(`${dateString}T23:59:59Z`)
  }
}

export function parseDateWithoutTimeZone(dateString: string) {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number)

  const parsedDate = parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date())

  return parsedDate
}

export const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes} MIN`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}H ${remainingMinutes}M` : `${hours}H`
}

export const formatTimeSlot = (at?: string | null, by?: string | null): string => {
  if (!by) return 'Time not specified'

  try {
    const byDate = new Date(by)
    const dateStr = format(byDate, 'dd MMM')

    if (!at) {
      return `${dateStr}, ${format(byDate, 'HH:mm')}`
    }

    const atTime = format(new Date(at), 'HH:mm')
    const byTime = format(byDate, 'HH:mm')

    return `${dateStr}, ${atTime} - ${byTime}`
  } catch {
    return 'Invalid date'
  }
}

export const formatDeliveryTime = (dateTime?: string) => {
  if (!dateTime) return undefined
  const date = new Date(dateTime)
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
}

export const validateDateRange = (fromDate: Date, toDate: Date) => {
  if (!isValid(fromDate) || !isValid(toDate)) return false

  const lastDayOfFromMonth = endOfMonth(fromDate)
  if (isAfter(toDate, lastDayOfFromMonth)) {
    return false
  }

  const firstDayOfToMonth = startOfMonth(toDate)
  if (isBefore(fromDate, firstDayOfToMonth)) {
    return false
  }

  if (isAfter(fromDate, toDate)) {
    return false
  }

  return true
}
